<template>
  <div class="section-spacing-20">
    <div class="d-flex justify-content-between align-items-center">
      <h4 class="font-normal">Add Widget to Your Site</h4>
      <a
        href="https://codesandbox.io/s/sera-widget-page-example-ezr5m"
        target="_blank"
      >
        <i class="fa-regular fa-rectangle-history-circle-plus mr-1"></i>View
        Live Example</a
      >
    </div>
    <div>
      <div class="mb-6">
        <b>1.</b> Include the following script tags into the
        <code>{{ head }}</code>
        of your document:
      </div>
      <textarea v-pre disabled class="code-preview" rows="6">
          <!-- SERA WIDGETS SCRIPTS -->&#10;
          <script
            defer
            src="https://cdnjs.cloudflare.com/ajax/libs/webcomponentsjs/2.2.10/webcomponents-bundle.js"
            integrity="sha256-aVBGxeTxC3EKdKExZpeda6waSlxIReqV4pi0nblYO7o="
            crossorigin="anonymous"
          ></script>
          <script
            defer
            src="https://cdn.sera.tech/widgets/v1/schedule/schedule-appointment.min.js"
          ></script>
          <!-- END SERA WIDGETS SCRIPTS -->
        </textarea
      >
      <div class="note-text">
        These scripts should only be loaded once per page.
      </div>
    </div>
    <div>
      <div class="mb-6">
        <b>2.</b> Include the following tag into the
        <code>{{ body }}</code>
        of your page where you would like the widget to appear:
      </div>
      <textarea disabled class="code-preview">
          <schedule-appointment tenant="{{
          tenant.subdomain
        }}">Loading Schedule Form ...</schedule-appointment>
        </textarea
      >
      <div class="note-text">
        Multiple widgets can be included on a single page.
      </div>
    </div>
    <div>
      <b>That's it!</b> The widget should be displayed on the page ready to
      schedule appointments.
    </div>
    <h4 class="mt-4 font-normal">Track Conversions</h4>
    <div>
      <div class="mb-6">
        Our scheduling widget will fire pre-defined events as users interact
        with it. These events can be sent directly to your Google Tag Manager
        account by adding a "<b>gtag-id</b>" attribute to the
        <code class="whitespace-nowrap">{{ scheduleTag }}</code> tag:
      </div>
      <textarea disabled class="code-preview">
          <schedule-appointment tenant="{{ tenant.subdomain }}" gtag-id="{{
          getValue("google_analytics_id") || "GOOGLE_ANALYTICS_ID"
        }}">Loading Schedule Form ...</schedule-appointment>
        </textarea
      >
    </div>
    <div>
      The widget will fire "<b>page views</b>" for each step of the process:
      <ul class="mt-2">
        <li>/step1</li>
        <li>/step2</li>
        <li>/confirmation</li>
        <li>/area_not_covered</li>
        <li>/no_appointments_available</li>
      </ul>
      <b>Events</b>
      <ul>
        <li class="my-2"><b>Appointment Scheduled</b></li>
        <ul>
          <li>event_category = Conversion</li>
          <li>
            Fired after a user successfully books an appointment and lands on
            the confirmation screen of the widget
          </li>
        </ul>
        <li class="my-2"><b>Area Not Covered</b></li>
        <ul>
          <li>event_category = Interested Customer</li>
          <li>
            Fired after a users information is captured but their zip code is
            not in the company's coverage area
          </li>
        </ul>
        <li class="my-2"><b>No Appointments Available</b></li>
        <ul>
          <li>event_category = Interested Customer</li>
          <li>
            Fired after a user requests availability for a given service
            category and there are no available times to select
          </li>
        </ul>
      </ul>
      <div class="mb-6"><b>Add Custom Conversion Events</b></div>
      <p class="mb-6">
        We also support adding custom conversion events using the
        “<b>gtag-conversion-events</b>” attribute which accepts an array of
        Google Analytic events that the widget will fire after a successful
        conversion:
      </p>
      <textarea disabled class="code-preview" rows="3">
          <schedule-appointment tenant="{{ tenant.subdomain }}" gtag-id="{{
          getValue("google_analytics_id") || "GOOGLE_ANALYTICS_ID"
        }}" gtag-conversion-events="[{'event_name':'conversion','event_params':{'send_to':'YOUR_TRACKING_ID'}]" >Loading Schedule Form ...</schedule-appointment>
        </textarea
      >
    </div>
    <Table>
      <thead>
        <tr>
          <th></th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>event_name</td>
          <td>
            <ul class="ml-0 pl-0">
              <li class="whitespace-normal">
                A recommended event. Each recommended event can take recommended
                parameters.
              </li>
              <li class="whitespace-normal">
                A custom event is an arbitrary event name that you make up, with
                arbitrary (i.e. custom) parameters. For example, see how custom
                events are used in Google Analytics.
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>event_params</td>
          <td>
            One or more parameter-value pairs, each pair separated by a comma.
          </td>
        </tr>
      </tbody>
    </Table>
    <div>
      See more in the
      <a :href="widgetSetupInstructionsLink">Knowledge Center</a>.
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, ref } from "vue";
import { getProperty } from "@/lib/helpers";
import { useStore } from "vuex";

import Table from "@/components/Table.vue";
import { useTenantCustomizations } from "../useTenantCustomization";

const store = useStore();
const { getValue } = useTenantCustomizations();

const head = ref("<head>");
const body = ref("<body>");
const scheduleTag = ref("<schedule-appointment>");

const tenant = computed(() => store.state.tenant);

const widgetSetupInstructionsLink: ComputedRef<string> = computed(() => {
  const link = getProperty(
    store.state.tenantCustomizations,
    "System.widget_script_instructions_link"
  );
  return link?.value || link?.default;
});
</script>

<style lang="scss" scoped>
.code-preview {
  width: 100%;
  border-radius: 5px;
  padding: 5px;
  white-space: pre-wrap;
  font-size: 0.9em;
}
.note-text {
  font-size: 0.8em;
  color: var(--gray);
}
</style>
